import PlusIcon from '@heroicons/react/24/outline/PlusIcon';
//@ts-ignore: Missing types in `beautiful-react-hooks`
import useDebouncedCallback from 'beautiful-react-hooks/useDebouncedCallback';
import { useCallback, useState } from 'react';

import { Badge } from '@/components/core/Badge';
import { Button } from '@/components/core/Button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogHeaderDefault } from '@/components/core/Dialog';
import { Loader } from '@/components/core/Loader';
import { SearchInput } from '@/components/core/SearchInput/SearchInput';
import { useTranslation } from '@/hooks/useTranslation';
import { useItemSearch } from '@/pages/dam/api/linkedItems.queries';
import { cn } from '@/services/utils';
import type { PersistedItem } from '@/types/DataModel';

const SEARCH_DEBOUNCE_MS = 300;

interface DamLinkArticlesDialogProps {
    /** One-line description of an item (product name and the `asset_linked_items` columns). */
    describe: (item: PersistedItem) => string;
    linkedKeys: Set<string>;
    /** Links the item at once; the dialog stays open (Make "Add Article"). */
    onLink: (item: PersistedItem) => Promise<unknown>;
    onClose: () => void;
}

/**
 * "Add article" (Make): search over the items, "Add" per result or "Linked" when the asset
 * is already linked; each "Add" links at once, "Done" closes the dialog.
 */
function DamLinkArticlesDialog({ describe, linkedKeys, onLink, onClose }: DamLinkArticlesDialogProps) {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [keyword, setKeyword] = useState('');
    const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());

    const commitKeyword = useCallback((value: string) => setKeyword(value.trim()), []);
    const applyKeyword = useDebouncedCallback(commitKeyword, [commitKeyword], SEARCH_DEBOUNCE_MS);

    const { data: items = [], isFetching } = useItemSearch({ variables: { keyword }, enabled: keyword.length > 0 });

    const link = async (item: PersistedItem) => {
        const key = item.primaryKey__;
        setPendingKeys((current) => new Set(current).add(key));
        try {
            await onLink(item);
        } finally {
            setPendingKeys((current) => {
                const next = new Set(current);
                next.delete(key);
                return next;
            });
        }
    };

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent variant="large">
                <DialogHeader>
                    <DialogHeaderDefault closeLabel={t('CLOSE')}>{t('DAM.DETAIL.ADD_ARTICLE')}</DialogHeaderDefault>
                </DialogHeader>
                <DialogBody className="tw-flex tw-flex-col tw-gap-4">
                    <p className="tw-m-0 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">{t('DAM.DETAIL.ADD_ARTICLE_TEXT')}</p>
                    <SearchInput
                        defaultOpen
                        aria-label={t('DAM.DETAIL.SEARCH_ARTICLES')}
                        placeholder={t('DAM.DETAIL.SEARCH_ARTICLES')}
                        value={searchTerm}
                        onChange={(value: string) => {
                            setSearchTerm(value);
                            applyKeyword(value);
                        }}
                    />
                    {isFetching ? (
                        <div className="tw-flex tw-justify-center tw-py-4"><Loader /></div>
                    ) : keyword && items.length === 0 ? (
                        <p className="tw-m-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{t('DAM.DETAIL.NO_ARTICLES_FOUND')}</p>
                    ) : (
                        <ul className="tw-m-0 tw-flex tw-max-h-96 tw-list-none tw-flex-col tw-overflow-y-auto tw-p-0">
                            {items.map((item) => {
                                const key = item.primaryKey__;
                                const isLinked = linkedKeys.has(key);
                                return (
                                    <li
                                        key={key}
                                        className={cn(
                                            'tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-2',
                                            'tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-divider first:tw-border-t-0',
                                        )}
                                    >
                                        <span className="tw-min-w-0 tw-break-words tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">{describe(item)}</span>
                                        {isLinked ? (
                                            <Badge variant="neutral" size="xl">{t('DAM.DETAIL.LINKED')}</Badge>
                                        ) : (
                                            <Button
                                                variant="outline"
                                                hasIcon
                                                icon={PlusIcon}
                                                loading={pendingKeys.has(key)}
                                                disabled={pendingKeys.has(key)}
                                                onClick={() => void link(item)}
                                            >
                                                {t('DAM.DETAIL.ADD')}
                                            </Button>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button onClick={onClose}>{t('DAM.DETAIL.DONE')}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { DamLinkArticlesDialog };
export type { DamLinkArticlesDialogProps };
